import React, { useEffect, useRef, useState } from 'react';
import { loadRetentionPage } from '../../lib/retentionReferences';

const defaultDay = () => new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);

export default function RetentionReferences() {
  const [kind, setKind] = useState('contact');
  const [day, setDay] = useState(defaultDay);
  const [page, setPage] = useState(null);
  const [pageNumber, setPageNumber] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const activeRequest = useRef(null);
  const version = useRef(0);
  useEffect(() => () => { version.current += 1; activeRequest.current?.abort(); }, []);

  const reset = () => {
    version.current += 1;
    activeRequest.current?.abort();
    setLoading(false); setPage(null); setPageNumber(0); setError('');
  };
  const load = async (next = false) => {
    const current = ++version.current;
    activeRequest.current?.abort();
    const request = new AbortController();
    activeRequest.current = request;
    const after = next ? page?.next_cursor : null;
    const number = next ? pageNumber + 1 : 1;
    setLoading(true); setError(''); setPage(null);
    try {
      const result = await loadRetentionPage({ kind, day, after, signal: request.signal });
      if (current === version.current) { setPage(result); setPageNumber(number); }
    } catch (err) {
      if (current === version.current && !request.signal.aborted) setError(err.message);
    } finally { if (current === version.current) setLoading(false); }
  };
  const download = () => {
    if (!page || loading || !page.entries.length) return;
    let url;
    try {
      url = URL.createObjectURL(new Blob([JSON.stringify(page, null, 2)], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `riferimenti_${page.kind}_${day}_pagina_${pageNumber}.json`;
      link.click();
    } catch { setError('Download non disponibile in questo browser. Nessuna copia esterna modificata.'); }
    finally { if (url) setTimeout(() => URL.revokeObjectURL(url), 1000); }
  };
  return <section className="bg-white rounded-2xl p-6 shadow-sm text-gray-700" aria-labelledby="retention-title">
    <h2 id="retention-title" className="text-2xl font-bold text-[#0b114e] mb-4">Riferimenti per la conservazione</h2>
    <p className="mb-3">Solo metadati di richieste e appuntamenti, senza nomi, email, messaggi o orari prenotati. Richiede admin con MFA. Nessun invio o cancellazione.</p>
    <p className="mb-6 text-sm">Prepara le scadenze prima della pulizia del database: i riferimenti scompaiono insieme al record. Ogni pagina contiene al massimo 100 voci e non è uno snapshot atomico. Gli ID Resend non sono gli ID della casella email. Verifica ogni copia separatamente e conserva gli export ad accesso ristretto, fuori da Git.</p>
    <div className="flex flex-wrap gap-4 items-end mb-6">
      <label className="flex flex-col gap-2">Archivio
        <select className="border rounded-lg p-2" value={kind} onChange={event => { reset(); setKind(event.target.value); }}>
          <option value="contact">Richieste di contatto</option><option value="appointment">Appuntamenti</option>
        </select>
      </label>
      <label className="flex flex-col gap-2">Scadenze entro (giorno UTC incluso)
        <input className="border rounded-lg p-2" type="date" value={day} onChange={event => { reset(); setDay(event.target.value); }} />
      </label>
      <button className="px-4 py-2 rounded-lg bg-[#0b114e] text-white disabled:opacity-50" type="button" disabled={loading || !day} onClick={() => load()}>Leggi prima pagina</button>
    </div>
    <div aria-live="polite">{loading && <p>Caricamento dei riferimenti…</p>}{error && <p role="alert" className="text-red-700">{error}</p>}</div>
    {page && <>
      <p className="mb-3">Pagina {pageNumber}: {page.entries.length} riferimenti. Nessuna copia esterna modificata.</p>
      {page.entries.length > 0 ? <div role="list" aria-label="Riferimenti della pagina corrente" className="mb-4 space-y-3">
        {page.entries.map(entry => <div role="listitem" key={entry.id} className="border rounded-lg p-3 text-sm">
          <p className="font-mono break-all">{entry.reference}</p>
          <p>Scadenza UTC: {entry.expires_at}</p>
          <p>Tracciamento email: {entry.notification_email_id ? 'ID disponibile' : 'Da riconciliare; non prova mancato invio'}</p>
        </div>)}
      </div> : <p className="mb-4">Nessun record entro questa data. Non è una prova dell’assenza di copie nella posta o in Calendar.</p>}
      <div className="flex flex-wrap gap-3">
        <button className="border rounded-lg px-4 py-2 disabled:opacity-50" type="button" disabled={!page.entries.length || loading} onClick={download}>Scarica pagina JSON</button>
        {page.has_more && <button className="border rounded-lg px-4 py-2" type="button" disabled={loading} onClick={() => load(true)}>Pagina successiva</button>}
      </div>
    </>}
  </section>;
}
